import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, eachDayOfInterval, isAfter, isSameDay, isToday } from 'date-fns';
import { PlusIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface Habit {
  id: string;
  name: string;
  description: string | null;
  frequency_type: 'daily' | 'weekly' | 'monthly';
  target_count: number;
  is_active: boolean;
  daily_limit: number | null;
  track_quantity: boolean;
}

interface HabitLog {
  id: string;
  habit_id: string;
  completed_at: string;
  notes: string | null;
  completion_count: number;
  completion_value: number;
}

function HabitTracker() {
  const [habits, setHabits] = useState<Habit[]>([]);
  const [habitLogs, setHabitLogs] = useState<HabitLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewHabitForm, setShowNewHabitForm] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState<Date>(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [newHabit, setNewHabit] = useState<Partial<Habit>>({
    name: '',
    description: '',
    frequency_type: 'weekly',
    target_count: 1,
    is_active: true,
    daily_limit: null,
    track_quantity: false,
  });

  useEffect(() => {
    fetchHabits();
    fetchHabitLogs();
  }, [selectedWeek]);

  const fetchHabits = async () => {
    try {
      const { data, error } = await supabase
        .from('habits')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setHabits(data || []);
    } catch (err: any) {
      console.error('Error fetching habits:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchHabitLogs = async () => {
    try {
      const weekStart = startOfWeek(selectedWeek, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(selectedWeek, { weekStartsOn: 1 });
      
      const { data, error } = await supabase
        .from('habit_logs')
        .select('*')
        .gte('completed_at', weekStart.toISOString())
        .lte('completed_at', weekEnd.toISOString())
        .order('completed_at', { ascending: false });

      if (error) throw error;
      setHabitLogs(data || []);
    } catch (err: any) {
      console.error('Error fetching habit logs:', err);
    }
  };

  const getCompletionData = (habit: Habit, date?: Date) => {
    const relevantLogs = habitLogs.filter(log => {
      const logDate = new Date(log.completed_at);
      return log.habit_id === habit.id && (!date || isSameDay(logDate, date));
    });

    const totalCount = relevantLogs.reduce((sum, log) => sum + log.completion_count, 0);
    const totalValue = relevantLogs.reduce((sum, log) => sum + log.completion_value, 0);

    return {
      count: totalCount,
      value: totalValue,
      logs: relevantLogs,
    };
  };

  const handleCreateHabit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('habits')
        .insert([
          {
            ...newHabit,
            user_id: user.id,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      setHabits([data, ...habits]);
      setShowNewHabitForm(false);
      setNewHabit({
        name: '',
        description: '',
        frequency_type: 'weekly',
        target_count: 1,
        is_active: true,
        daily_limit: null,
        track_quantity: false,
      });
    } catch (err: any) {
      console.error('Error creating habit:', err);
      setError(err.message);
    }
  };

  const handleLogHabit = async (habit: Habit, date: Date, value: number = 1) => {
    try {
      if (isAfter(date, new Date())) {
        throw new Error("Cannot log habits for future dates");
      }

      if (habit.daily_limit !== null) {
        const dailyData = getCompletionData(habit, date);
        if (dailyData.count >= habit.daily_limit) {
          throw new Error(`Daily limit of ${habit.daily_limit} reached`);
        }
      }

      const { data, error } = await supabase
        .from('habit_logs')
        .insert([
          {
            habit_id: habit.id,
            completed_at: date.toISOString(),
            completion_count: 1,
            completion_value: value,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      setHabitLogs([data, ...habitLogs]);
    } catch (err: any) {
      console.error('Error logging habit:', err);
      setError(err.message);
    }
  };

  const handlePreviousWeek = () => {
    setSelectedWeek(prev => subWeeks(prev, 1));
  };

  const handleNextWeek = () => {
    const nextWeek = addWeeks(selectedWeek, 1);
    if (!isAfter(nextWeek, new Date())) {
      setSelectedWeek(nextWeek);
    }
  };

  const getDaysOfWeek = () => {
    return eachDayOfInterval({
      start: startOfWeek(selectedWeek, { weekStartsOn: 1 }),
      end: endOfWeek(selectedWeek, { weekStartsOn: 1 })
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-gray-800"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Habits</h2>
          <p className="mt-1 text-sm text-gray-500">Track your daily and weekly habits</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
          <div className="flex items-center space-x-2 bg-white rounded-lg shadow-sm p-2">
            <button
              onClick={handlePreviousWeek}
              className="p-1 rounded-full hover:bg-gray-100"
            >
              <ChevronLeftIcon className="h-5 w-5 text-gray-500" />
            </button>
            <span className="text-sm font-medium text-gray-700 min-w-[120px] text-center">
              Week of {format(selectedWeek, 'MMM d')}
            </span>
            <button
              onClick={handleNextWeek}
              disabled={isAfter(addWeeks(selectedWeek, 1), new Date())}
              className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50"
            >
              <ChevronRightIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>
          <button
            onClick={() => setShowNewHabitForm(!showNewHabitForm)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            {showNewHabitForm ? (
              <>
                <XMarkIcon className="h-5 w-5 mr-2" />
                Cancel
              </>
            ) : (
              <>
                <PlusIcon className="h-5 w-5 mr-2" />
                New Habit
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 text-red-700 bg-red-50 rounded-lg border border-red-200">
          {error}
          <button
            onClick={() => setError(null)}
            className="float-right text-red-500 hover:text-red-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}

      {showNewHabitForm && (
        <form onSubmit={handleCreateHabit} className="mb-8 bg-white p-6 rounded-lg shadow">
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Habit Name
              </label>
              <input
                type="text"
                id="name"
                value={newHabit.name}
                onChange={(e) => setNewHabit({ ...newHabit, name: e.target.value })}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description (Optional)
              </label>
              <textarea
                id="description"
                value={newHabit.description || ''}
                onChange={(e) => setNewHabit({ ...newHabit, description: e.target.value })}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="frequency" className="block text-sm font-medium text-gray-700">
                  Frequency
                </label>
                <select
                  id="frequency"
                  value={newHabit.frequency_type}
                  onChange={(e) => setNewHabit({ ...newHabit, frequency_type: e.target.value as 'daily' | 'weekly' | 'monthly' })}
                  className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              <div>
                <label htmlFor="target" className="block text-sm font-medium text-gray-700">
                  Target Count
                </label>
                <input
                  type="number"
                  id="target"
                  min="1"
                  value={newHabit.target_count}
                  onChange={(e) => setNewHabit({ ...newHabit, target_count: parseInt(e.target.value) })}
                  className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="daily_limit" className="block text-sm font-medium text-gray-700">
                  Daily Limit (Optional)
                </label>
                <input
                  type="number"
                  id="daily_limit"
                  min="1"
                  value={newHabit.daily_limit || ''}
                  onChange={(e) => setNewHabit({ 
                    ...newHabit, 
                    daily_limit: e.target.value ? parseInt(e.target.value) : null 
                  })}
                  className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-gray-500 focus:ring-gray-500"
                  placeholder="No limit"
                />
              </div>

              <div className="flex items-center">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={newHabit.track_quantity}
                    onChange={(e) => setNewHabit({ ...newHabit, track_quantity: e.target.checked })}
                    className="rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                  />
                  <span className="text-sm text-gray-700">
                    Track quantity (e.g., minutes, kilometers)
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Create Habit
            </button>
          </div>
        </form>
      )}

      <div className="space-y-6">
        {habits.map((habit) => {
          const weeklyData = getCompletionData(habit);
          const weeklyProgress = habit.track_quantity
            ? (weeklyData.value / habit.target_count) * 100
            : (weeklyData.count / habit.target_count) * 100;
          const daysOfWeek = getDaysOfWeek();

          return (
            <div key={habit.id} className="bg-white p-4 sm:p-6 rounded-lg shadow-sm hover:shadow transition-shadow">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900">{habit.name}</h3>
                {habit.description && (
                  <p className="text-sm text-gray-500 mt-1">{habit.description}</p>
                )}
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                  <span>Weekly Progress</span>
                  <span className="font-medium">
                    {habit.track_quantity ? weeklyData.value : weeklyData.count} / {habit.target_count} {habit.frequency_type}
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-2 bg-green-500 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(100, weeklyProgress)}%` }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-7 gap-2">
                {daysOfWeek.map((date) => {
                  const dailyData = getCompletionData(habit, date);
                  const isFuture = isAfter(date, new Date());
                  const isDisabled = isFuture || (
                    habit.daily_limit !== null && dailyData.count >= habit.daily_limit
                  );

                  return (
                    <div key={date.toISOString()} className="text-center">
                      <div className="text-xs font-medium text-gray-500 mb-1">
                        {format(date, 'EEE')}
                      </div>
                      <div className="text-xs text-gray-500 mb-2">
                        {format(date, 'd')}
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        {dailyData.count > 0 && (
                          <div className={`text-xs font-medium ${isToday(date) ? 'text-green-600' : 'text-gray-700'}`}>
                            {habit.track_quantity ? dailyData.value : dailyData.count}
                          </div>
                        )}
                        <button
                          onClick={() => handleLogHabit(habit, date)}
                          disabled={isDisabled}
                          className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                            dailyData.count > 0
                              ? 'bg-green-500 text-white'
                              : isDisabled
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}
                          title={
                            isDisabled
                              ? isFuture
                                ? "Can't log future dates"
                                : `Daily limit of ${habit.daily_limit} reached`
                              : undefined
                          }
                        >
                          {dailyData.count > 0 ? (
                            <CheckIcon className="h-4 w-4" />
                          ) : (
                            <PlusIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {habits.length === 0 && !showNewHabitForm && (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            <p className="text-gray-500 mb-4">No habits created yet</p>
            <button
              onClick={() => setShowNewHabitForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create Your First Habit
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default HabitTracker;